import { AccountsApiError, ACCOUNTS_API_URL, patchTrustWindow } from '../account/api';
import { addPasskey, signInWithPasskey, signUp, unlockMasterKey } from '../account/webauthn';
import * as api from '../account/api';
import {
  clearStoredSession,
  getDeviceId,
  getMasterKey,
  getStoredSession,
  setStoredSession,
  setTrustWindowDays,
} from '../account/session';
import { pullApiKey, pullGithubToken, pushApiKey, pushGithubToken } from '../account/sync';
import { getCumulativeBenchmarkStats, type BenchmarkStats } from '../db/benchmarks-repo';
import { createThemeToggle } from './theme';

export interface AccountPanelCallbacks {
  /** Current value of the local API key input, so "Push current key" knows what to encrypt. */
  getCurrentApiKey: () => string;
  /** Called when a synced key was pulled and unwrapped — the caller should adopt it locally. */
  onApiKeyReceived: (key: string) => void;
  /** Reads the currently-saved local GitHub token (from IndexedDB), for "Push current token" to encrypt. */
  getCurrentGithubToken: () => Promise<string>;
  /** Called when a synced token was pulled and unwrapped — the caller should persist it locally. */
  onGithubTokenReceived: (token: string) => void;
  /** Called right after a successful sign-up/sign-in, so the caller can pull settings/conversations. */
  onLoggedIn: () => void;
  /** Called at the end of every render — lets a wrapper (e.g. a modal's trigger button) stay in sync. */
  onStateChanged?: () => void;
}

export class AccountPanel {
  private root: HTMLElement;
  private callbacks: AccountPanelCallbacks;
  private pendingOtp: { email: string } | null = null;
  private busy = false;
  private statusText = '';
  private statusIsError = false;
  private deviceStats: BenchmarkStats | null = null;

  constructor(container: HTMLElement, callbacks: AccountPanelCallbacks) {
    this.root = container;
    this.callbacks = callbacks;
    this.render();
    void getCumulativeBenchmarkStats().then((stats) => {
      this.deviceStats = stats;
      this.render();
    });
  }

  private setStatus(text: string, isError = false) {
    this.statusText = text;
    this.statusIsError = isError;
    this.render();
  }

  private async run(fn: () => Promise<void>) {
    if (this.busy) return;
    this.busy = true;
    this.render();
    try {
      await fn();
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : String(err), true);
    } finally {
      this.busy = false;
      this.render();
    }
  }

  private render() {
    this.root.innerHTML = '';
    this.renderAppearance();
    this.renderDeviceStats();
    this.renderTelemetryNote();

    if (!ACCOUNTS_API_URL) {
      const note = document.createElement('div');
      note.className = 'account-note';
      note.textContent = 'Account sync is not configured for this build.';
      this.root.appendChild(note);
      this.callbacks.onStateChanged?.();
      return;
    }

    const session = getStoredSession();
    if (!session) {
      this.renderLoggedOut();
    } else {
      this.renderLoggedIn(session);
    }

    if (this.statusText) {
      const status = document.createElement('div');
      status.className = `account-status${this.statusIsError ? ' error' : ''}`;
      status.textContent = this.statusText;
      this.root.appendChild(status);
    }

    this.callbacks.onStateChanged?.();
  }

  /** Theme control lives here so it's reachable from the mobile Account tab as well as the rail. */
  private renderAppearance() {
    const section = document.createElement('div');
    section.className = 'account-section account-appearance';
    const label = document.createElement('span');
    label.className = 'account-section-label';
    label.textContent = 'Appearance';
    section.appendChild(label);
    section.appendChild(createThemeToggle());
    this.root.appendChild(section);
  }

  /** This device's lifetime totals — the same numbers the Benchmarks card reports, in context. */
  private renderDeviceStats() {
    if (!this.deviceStats) return;
    const stats = this.deviceStats;
    const saved = Math.max(0, stats.tokensBefore - stats.tokensAfter);
    const pct = stats.tokensBefore > 0 ? (saved / stats.tokensBefore) * 100 : 0;
    const section = document.createElement('div');
    section.className = 'account-section';
    section.innerHTML = `
      <span class="account-section-label">This device — lifetime</span>
      <div class="account-stat-grid">
        <div class="stat"><div class="label">Requests</div><div class="value mono">${stats.requests.toLocaleString()}</div></div>
        <div class="stat"><div class="label">Est. cost</div><div class="value mono">$${stats.costUsd.toFixed(4)}</div></div>
        <div class="stat savings"><div class="label">Tokens saved</div><div class="value mono">${
          stats.tokensBefore > 0 ? `${saved.toLocaleString()} (${pct.toFixed(0)}%)` : '—'
        }</div></div>
      </div>`;
    this.root.appendChild(section);
  }

  /**
   * Tier-1 telemetry disclosure. Rendered before the `ACCOUNTS_API_URL` bail-out above so it shows
   * on every build, signed in or not — this collection isn't gated on having an account.
   */
  private renderTelemetryNote() {
    const section = document.createElement('div');
    section.className = 'account-section';
    const label = document.createElement('span');
    label.className = 'account-section-label';
    label.textContent = 'Anonymous usage data';
    section.appendChild(label);
    const note = document.createElement('p');
    note.className = 'account-hero-copy';
    note.textContent =
      "Every request sends anonymous metadata to the project's servers: model, which optimization stages ran and what they saved, token counts, timings, browser, and the language mix of your open files. It's tagged with a random ID for this browser, contains no prompts or file contents, and is sent whether or not you're signed in.";
    section.appendChild(note);
    this.root.appendChild(section);
  }

  private renderLoggedOut() {
    const hero = document.createElement('div');
    hero.className = 'account-hero';
    hero.innerHTML = `
      <span class="account-section-label">Sync across devices</span>
      <p class="account-hero-copy">Your API key never leaves this browser unencrypted. Sign in with a passkey to sync it, your
      settings and your conversations to your other devices — end-to-end encrypted, unlockable only by your passkey.</p>`;
    this.root.appendChild(hero);

    const emailRow = document.createElement('div');
    emailRow.className = 'account-row';
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.placeholder = 'you@example.com';
    emailInput.className = 'account-email-input';
    emailRow.appendChild(emailInput);
    this.root.appendChild(emailRow);

    if (this.pendingOtp) {
      const otpRow = document.createElement('div');
      otpRow.className = 'account-row account-otp-row';
      const otpInput = document.createElement('input');
      otpInput.type = 'text';
      otpInput.inputMode = 'numeric';
      otpInput.maxLength = 6;
      otpInput.placeholder = '6-digit code';
      otpRow.appendChild(otpInput);
      const verifyBtn = document.createElement('button');
      verifyBtn.className = 'btn primary';
      verifyBtn.textContent = 'Verify code';
      verifyBtn.disabled = this.busy;
      verifyBtn.addEventListener('click', () => {
        const email = this.pendingOtp!.email;
        const code = otpInput.value.trim();
        void this.run(async () => {
          const result = await api.otpVerify(email, getDeviceId(), code);
          setStoredSession({ sessionToken: result.sessionToken, userId: result.userId, email, trustWindowDays: 7 });
          const me = await api.accountMe().catch(() => null);
          if (me) setStoredSession({ sessionToken: result.sessionToken, userId: result.userId, email, trustWindowDays: me.trustWindowDays });
          this.pendingOtp = null;
          this.setStatus('Signed in.');
          this.callbacks.onLoggedIn();
        });
      });
      otpRow.appendChild(verifyBtn);
      const cancelBtn = document.createElement('button');
      cancelBtn.className = 'btn subtle';
      cancelBtn.textContent = 'Cancel';
      cancelBtn.addEventListener('click', () => {
        this.pendingOtp = null;
        this.setStatus('');
      });
      otpRow.appendChild(cancelBtn);
      this.root.appendChild(otpRow);
      return;
    }

    const btnRow = document.createElement('div');
    btnRow.className = 'account-row account-cta-row';

    const signUpBtn = document.createElement('button');
    signUpBtn.className = 'btn primary';
    signUpBtn.textContent = 'Create account with passkey';
    signUpBtn.disabled = this.busy;
    signUpBtn.addEventListener('click', () => {
      const email = emailInput.value.trim();
      if (!email) return this.setStatus('Enter an email address first.', true);
      void this.run(async () => {
        const { prfSupported } = await signUp(email);
        this.setStatus(
          prfSupported
            ? 'Account created and signed in.'
            : "Account created and signed in. This browser/device doesn't support secure key sync (the PRF extension) — add a passkey on one that does (most security keys, Chrome, or Edge) to sync your API key.",
        );
        this.callbacks.onLoggedIn();
      });
    });
    btnRow.appendChild(signUpBtn);

    const signInBtn = document.createElement('button');
    signInBtn.className = 'btn';
    signInBtn.textContent = 'Sign in';
    signInBtn.disabled = this.busy;
    signInBtn.addEventListener('click', () => {
      const email = emailInput.value.trim();
      if (!email) return this.setStatus('Enter an email address first.', true);
      void this.run(() => this.attemptSignIn(email));
    });
    btnRow.appendChild(signInBtn);

    this.root.appendChild(btnRow);

    const hint = document.createElement('div');
    hint.className = 'account-hint';
    hint.textContent =
      'New or untrusted devices verify with a passkey. Devices you already trust here can sign in with just email + a one-time code.';
    this.root.appendChild(hint);
  }

  private async attemptSignIn(email: string): Promise<void> {
    try {
      await api.otpRequest(email, getDeviceId());
      this.pendingOtp = { email };
      this.setStatus('Enter the code we emailed you.');
    } catch (err) {
      if (err instanceof AccountsApiError && err.status === 403) {
        await signInWithPasskey(email);
        this.setStatus('Signed in with passkey.');
        this.callbacks.onLoggedIn();
      } else {
        throw err;
      }
    }
  }

  private renderLoggedIn(session: { email: string; trustWindowDays: number }) {
    const info = document.createElement('div');
    info.className = 'account-identity';
    info.innerHTML = `<span class="account-avatar mono">${escapeHtml((session.email || '?').slice(0, 1).toUpperCase())}</span>
      <span class="account-identity-text"><span class="account-info">${escapeHtml(session.email)}</span>
      <span class="account-hint">Signed in · synced</span></span>`;
    this.root.appendChild(info);

    const trustRow = document.createElement('div');
    trustRow.className = 'account-row';
    const trustLabel = document.createElement('span');
    trustLabel.textContent = 'Trust this device for';
    trustRow.appendChild(trustLabel);
    const trustSelect = document.createElement('select');
    for (const days of [1, 7, 30]) {
      const opt = document.createElement('option');
      opt.value = String(days);
      opt.textContent = `${days} day${days === 1 ? '' : 's'}`;
      opt.selected = days === session.trustWindowDays;
      trustSelect.appendChild(opt);
    }
    trustSelect.addEventListener('change', () => {
      const days = Number(trustSelect.value) as 1 | 7 | 30;
      void this.run(async () => {
        await patchTrustWindow(days);
        setTrustWindowDays(days);
        this.setStatus(`Trust window set to ${days} day(s).`);
      });
    });
    trustRow.appendChild(trustSelect);
    this.root.appendChild(trustRow);

    const keyRow = document.createElement('div');
    keyRow.className = 'account-row account-key-row';
    const locked = !getMasterKey();
    const keyStatus = document.createElement('span');
    keyStatus.className = `key-status ${locked ? 'locked' : 'unlocked'}`;
    keyStatus.textContent = locked ? 'Synced key locked' : 'Synced key unlocked';
    keyRow.appendChild(keyStatus);

    if (locked) {
      const unlockBtn = document.createElement('button');
      unlockBtn.className = 'btn';
      unlockBtn.textContent = 'Unlock with passkey';
      unlockBtn.disabled = this.busy;
      unlockBtn.addEventListener('click', () => {
        void this.run(async () => {
          await unlockMasterKey();
          const key = await pullApiKey();
          if (key) this.callbacks.onApiKeyReceived(key);
          this.setStatus('Synced key unlocked.');
        });
      });
      keyRow.appendChild(unlockBtn);
    } else {
      const pushBtn = document.createElement('button');
      pushBtn.className = 'btn';
      pushBtn.textContent = 'Push current key';
      pushBtn.disabled = this.busy;
      pushBtn.addEventListener('click', () => {
        void this.run(async () => {
          await pushApiKey(this.callbacks.getCurrentApiKey());
          this.setStatus('API key synced.');
        });
      });
      keyRow.appendChild(pushBtn);

      const pullBtn = document.createElement('button');
      pullBtn.className = 'btn';
      pullBtn.textContent = 'Pull synced key';
      pullBtn.disabled = this.busy;
      pullBtn.addEventListener('click', () => {
        void this.run(async () => {
          const key = await pullApiKey();
          if (key) {
            this.callbacks.onApiKeyReceived(key);
            this.setStatus('Synced key pulled.');
          } else {
            this.setStatus('No synced key found.');
          }
        });
      });
      keyRow.appendChild(pullBtn);

      const addPasskeyBtn = document.createElement('button');
      addPasskeyBtn.className = 'btn subtle';
      addPasskeyBtn.textContent = 'Add another passkey';
      addPasskeyBtn.disabled = this.busy;
      addPasskeyBtn.addEventListener('click', () => {
        void this.run(async () => {
          const { prfSupported } = await addPasskey();
          this.setStatus(
            prfSupported
              ? 'Passkey added.'
              : "Passkey added, but this browser/device doesn't support secure key sync (the PRF extension) — it'll work for signing in, just not for unlocking your synced API key.",
          );
        });
      });
      keyRow.appendChild(addPasskeyBtn);
    }
    this.root.appendChild(keyRow);

    this.renderGithubTokenRow(locked);

    const signOutRow = document.createElement('div');
    signOutRow.className = 'account-row';
    const signOutBtn = document.createElement('button');
    signOutBtn.className = 'btn subtle';
    signOutBtn.textContent = 'Sign out';
    signOutBtn.addEventListener('click', () => {
      clearStoredSession();
      this.setStatus('Signed out.');
    });
    signOutRow.appendChild(signOutBtn);
    this.root.appendChild(signOutRow);
  }

  /**
   * GitHub PAT sync, wrapped under the same master key K as the API key — so there is only ever one
   * lock. When locked, this row just reports that and points at the unlock button in the API-key row
   * above rather than offering a second, identical unlock.
   */
  private renderGithubTokenRow(locked: boolean) {
    const row = document.createElement('div');
    row.className = 'account-row account-key-row';

    const status = document.createElement('span');
    status.className = `key-status ${locked ? 'locked' : 'unlocked'}`;
    status.textContent = locked ? 'Synced GitHub token locked' : 'Synced GitHub token unlocked';
    row.appendChild(status);

    if (locked) {
      const hint = document.createElement('span');
      hint.className = 'account-hint key-row-hint';
      hint.textContent = 'Unlock with your passkey above to push or pull it — it uses the same key.';
      row.appendChild(hint);
    } else {
      const pushBtn = document.createElement('button');
      pushBtn.className = 'btn';
      pushBtn.textContent = 'Push current token';
      pushBtn.disabled = this.busy;
      pushBtn.addEventListener('click', () => {
        void this.run(async () => {
          const token = await this.callbacks.getCurrentGithubToken();
          await pushGithubToken(token);
          this.setStatus('GitHub token synced.');
        });
      });
      row.appendChild(pushBtn);

      const pullBtn = document.createElement('button');
      pullBtn.className = 'btn';
      pullBtn.textContent = 'Pull synced token';
      pullBtn.disabled = this.busy;
      pullBtn.addEventListener('click', () => {
        void this.run(async () => {
          const token = await pullGithubToken();
          if (token) {
            this.callbacks.onGithubTokenReceived(token);
            this.setStatus('Synced GitHub token pulled.');
          } else {
            this.setStatus('No synced GitHub token found.');
          }
        });
      });
      row.appendChild(pullBtn);
    }

    this.root.appendChild(row);
  }
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
